"use client";

import React from "react";
import { ArrowRight, Package, Ruler, CircleAlert, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 8,
  product_cat: 16
};
const wp_query = new WP_Query(wp_query_params);
function PlumbingGridConsumer() {
  const {
    products,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = useEcommerceProducts();
  if (loading && products.length === 0) {
    return <div data-section-id="149227" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="border-2 border-border p-4 h-[400px] flex flex-col gap-4 animate-pulse bg-muted/20 rounded-3xl">
            <div className="w-full h-48 bg-muted rounded-2xl" />
            <div className="h-4 bg-muted w-1/3" />
            <div className="h-6 bg-muted w-3/4" />
            <div className="mt-auto h-10 bg-muted w-full rounded-xl" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border-4 border-destructive p-8 flex flex-col items-center justify-center text-center bg-destructive/5 rounded-3xl">
        <CircleAlert className="size-12 text-destructive mb-4" />
        <h3 className="text-xl font-bold uppercase mb-2 text-foreground">Błąd ładowania systemów</h3>
        <p className="font-mono text-muted-foreground mb-6">{error}</p>
        <Button variant="outline" className="rounded-3xl border-2 font-mono uppercase" onClick={() => window.location.reload()}>
          Odśwież stronę
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="border-2 border-dashed border-border p-12 text-center rounded-3xl">
        <p className="font-mono text-muted-foreground uppercase">Obecnie brak dostępnych usług serwisowych</p>
      </div>;
  }
  return <div className="flex flex-col gap-12">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {products.map((product, i) => <article key={product.id} data-index={i} className="group relative flex flex-col border-2 border-blue-100 bg-card hover:border-primary hover:shadow-2xl transition-all duration-300 rounded-3xl overflow-hidden">
            {}
            <Link to={product.link} className="block aspect-square overflow-hidden border-b-2 border-blue-50 bg-muted relative">
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  <Package className="size-12 opacity-20" />
                </div>}
              {product.discounted_price && <Badge variant="destructive" className="absolute top-3 right-3 rounded-full font-mono uppercase tracking-wider bg-cyan-500 text-white border-none shadow-lg">
                  Serwis
                </Badge>}
            </Link>

            {}
            <div className="p-6 flex flex-col flex-grow">
              {}
              <div className="font-mono text-[11px] text-muted-foreground mb-3 uppercase tracking-wider flex items-center justify-between border-b border-border/50 pb-2">
                <span className="flex items-center gap-1">
                  <Package className="size-3" />
                  NR:{product.id.toString().padStart(6, "0")}
                </span>
                <span className="flex items-center gap-1">
                  <Ruler className="size-3" />
                  {product.customFields?.dimensions || "PŁUKANIE"}
                </span>
              </div>

              <Link to={product.link} className="group-hover:text-primary transition-colors">
                <h3 className="font-bold text-foreground leading-tight mb-3 line-clamp-2 uppercase text-sm tracking-tight">
                  {product.title}
                </h3>
              </Link>

              <div className="mt-auto pt-4 flex items-end justify-between">
                <div className="flex flex-col">
                  {product.discounted_price ? <>
                      <span className="text-xs text-muted-foreground line-through font-mono">
                        {product.price.toLocaleString("pl-PL")} PLN
                      </span>
                      <span className="text-xl font-black text-primary">
                        {product.discounted_price.toLocaleString("pl-PL")} PLN
                      </span>
                    </> : <span className="text-xl font-black text-foreground">
                      {product.price?.toLocaleString("pl-PL") || "0"} PLN
                    </span>}
                </div>
              </div>
            </div>

            {}
            <div className="p-6 pt-0">
              <AddToCartButton productId={product.id} className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-2xl font-mono uppercase tracking-widest text-sm py-6 border-2 border-transparent hover:shadow-lg transition-all" text="Zamów Serwis" showQuantity={false} />
            </div>
          </article>)}
      </div>

      {}
      {hasNext && <div className="flex justify-center mt-8">
          <Button variant="outline" size="lg" onClick={async () => await loadMore()} disabled={isRefetching} className="rounded-3xl border-2 border-primary text-primary font-mono uppercase tracking-widest hover:bg-primary hover:text-white transition-all px-12 py-6 shadow-md">
            {isRefetching ? <>
                <Loader className="mr-2 size-5 animate-spin" />
                Ładowanie...
              </> : "Zobacz więcej"}
          </Button>
        </div>}
    </div>;
}
export default function PlumbingAssortment() {
  return <section id="hydroprecision" className="bg-slate-50 py-16 md:py-24 border-y border-blue-100 overflow-hidden">
      <div className="container mx-auto px-4">
        {}
        <div className="mb-16 border-b border-blue-100 pb-10 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-2 w-12 bg-primary rounded-full" />
              <span className="font-mono text-sm font-bold uppercase tracking-widest text-blue-600">
                Nasze Rozwiązania
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-7xl font-black text-foreground uppercase tracking-tighter leading-[0.9] font-sans">
              Czysta Instalacja<br />
              <span className="text-primary">Niższe Rachunki</span>
            </h2>
            <p className="font-sans text-lg md:text-xl text-muted-foreground mt-8 max-w-2xl border-l-4 border-cyan-500 pl-6 leading-relaxed">
              PROFESJONALNE PŁUKANIE INSTALACJI CO. USUWAMY OSADY I KAMIEŃ, PRZYWRACAJĄC PEŁNĄ WYDAJNOŚĆ TWOJEGO SYSTEMU GRZEWCZEGO DLA MAKSYMALNEGO KOMFORTU.
            </p>
          </div>
          
          
        </div>
      </div>
    </section>;
}